import {Inject, Injectable} from 'ng-metadata/core';
import {STATE_NAMES} from '../../primoExplore/primoExploreStates.module';
import {getVid} from '../../appConfig/appConfigForward';
import {SearchStateService} from '../searchState.service';
import {preventStateReload} from '../../infra/annotations';
import {AuthoritySearchBarService} from './authoritySearchBar/authoritySearchBar.service';
import {SearchErrorMessageService} from "../searchResult/errorMessage/searchErrorMessage.service";
import {
    HandleLimitOffsetErrorResult,
    RestrictedOffsetHandlerService
} from "../../security/restrictedOffsetHandler.service";

@Injectable()
export class AuthoritySearchService {
    private _authorityData;
    private _authorityQuery: string;
    private _items: any[];
    private _offset: number = 0;
    private _sortby:any;
    private _authorityRequestFailed: boolean = false;
    private authSearchUrl = this.restBaseURLs.authoritySearchURL;

    constructor(@Inject('$state') private $state: angular.ui.IStateService,
        @Inject('$stateParams') private $stateParams: ng.ui.IStateParamsService,
        @Inject('restBaseURLs') private restBaseURLs,
        @Inject('$http') private $http: angular.IHttpService,
        @Inject('$q') private $q: ng.IQService,
        @Inject('$location') private $location: ng.ILocationService,
        @Inject('$rootScope') private $rootScope: ng.IRootScopeService,
        private searchStateService: SearchStateService,
        private authoritySearchBarService: AuthoritySearchBarService,
        private searchErrorMessageService: SearchErrorMessageService,
        private restrictedOffsetHandlerService: RestrictedOffsetHandlerService) {
    }

    public get authorityQuery(): string {
        return this._authorityQuery;
    }

    public set authorityQuery(value: string) {
        this._authorityQuery = value;
    }

    public get searchField() {
        return this.authoritySearchBarService.selectedScope;
    }

    public set searchField(value) {
        this.authoritySearchBarService.selectedScope = value;
    }

    public get searchOperator(): string {
        return this.authoritySearchBarService.selectedOperator;
    }

    public set searchOperator(value: string) {
        this.authoritySearchBarService.selectedOperator = value;
    }

    public get items() {
        return this._items;
    }

    public set items(value) {
        this._items = value;
    }

    public get inProgress() {
        return this.searchStateService.isSearchInProgress();
    }

    public set inProgress(value: boolean) {
        this.searchStateService.setSearchInProgress(value);   
     }

    public get authorityData() {
        return this._authorityData;
    }

    public set authorityData(value) {
        this._authorityData = value;
    }

    public get offset(): number {
        return this._offset;
    }

    public set offset(value: number) {
        this._offset = value;
    }

    public get authorityRequestFailed(): boolean {
        return this._authorityRequestFailed;
    }

    public set authorityRequestFailed(value: boolean) {
        this._authorityRequestFailed = value;
    }

    public isAuthorityState() {
        return (this.$state.current.name === STATE_NAMES.AUTHORITY_SEARCH || this.$state.current.name === STATE_NAMES.AUTHORITY_FULL_DISPLAY);
    }

    public async searchAuthorityRequest(searchTerm: string = "", searchField, limit: number) {
        try {
            let newOffset = this.getSearchObject().offset;
            if (newOffset != this.offset) {
                this.updateOffsetInUrl(newOffset);
            }

            let viewId: string = getVid();
            let config = {
                "params": {
                    "searchTerm": searchTerm,
                    "searchField": searchField,
                    "sort": this._sortby,
                    "offset": newOffset,
                    "limit": limit,
                    "vid": viewId,
                    "searchOperator": this.searchOperator,
                }
            };
            this.inProgress = true;
            this.searchStateService.setOffsetLimitNotificationVisible(false);
            let response;
            try {
              response = await this.$http.get(this.authSearchUrl, config)
            } catch (error) {
                if (this.restrictedOffsetHandlerService.isRestrictedOffsetError(error)) {
                    const result = await this.restrictedOffsetHandlerService.handleRestrictedOffsetError();
                    switch (result) {
                        case HandleLimitOffsetErrorResult.RETRY:
                            response = await this.$http.get(this.authSearchUrl, config);
                            break;

                        case HandleLimitOffsetErrorResult.USER_LOGGED_IN:
                            // Search is retried after user logs in, so we don't need to do anything here.
                            throw error;

                        case HandleLimitOffsetErrorResult.ERROR:
                            const lastViewedOffset = this.searchStateService.getLastViewedOffset();
                            this.getSearchObject().offset = lastViewedOffset;
                            config.params.offset = lastViewedOffset;
                            response = await this.$http.get(this.authSearchUrl, config);
                            this.offset = lastViewedOffset;
                            this.$state.go(STATE_NAMES.AUTHORITY_SEARCH, {offset: lastViewedOffset}, {notify: false});
                            this.searchStateService.setOffsetLimitNotificationVisible(true, true);
                    }
                } else {
                    throw error;
                }
            }
            this.authorityData = response.data;
            this.updateFields(this.authorityData);
            this.authorityRequestFailed = false;
            this.inProgress = false;
            return response.data;
        }
        catch (error) {
            this.inProgress = false;
            this.authorityRequestFailed = true;
            throw error;
        }
    }

    get sortby() {
        return this._sortby;
    }

    set sortby(sortby) {
        this._sortby = sortby;
    }

    public resetParams() {
        this.items = [];
        this.authorityData = undefined;
        if (this.$stateParams['offset']) {
            this.offset = parseInt(this.$stateParams['offset'], 10);
        } else {
            this.offset = 0;
        }
        if (this.searchField === undefined) {
            this.searchField = this.$stateParams['searchField'];
        }
    }

    public updateFields(data): void {
        this.items = data['docs'];
    }

    public getPreviousRecords(){
        let limit = this.getSearchObject().bulkSize;
        this.offset = this.offset - limit < 0 ? 0 : this.offset - limit;
        return this.doAuthorityQuery();
    }

    public getNextRecords(){
        let limit = this.getSearchObject().bulkSize;
        this.offset = this.offset + limit;
        return this.doAuthorityQuery();
    }

    public doAuthorityQuery(nextResults?: boolean) {
        if (!nextResults) {
            this.searchStateService.setSearchObject(null, true, this.offset);
        }
        let limit = this.getSearchObject().bulkSize;
        let p = this.searchAuthorityRequest(this.authorityQuery, this.searchField, limit).then(() => {
            return this.searchStateService.setResultObject(this.authorityData, false);
        });
        return p;
    }

    public getSearchObject() {
        return this.searchStateService.getSearchObject();
    }

    @preventStateReload
    updateOffsetInUrl(newOffset) {
        this.$location.search('offset', newOffset);
    }
}



// WEBPACK FOOTER //
// ./src/main/webapp/components/search/authority/authoritySearch.service.ts